import { eventBus } from '@/main'
import debounce from 'lodash/debounce'
import customDropdown from '@/components/dropdown/customDropdown.vue'
import TableButton from '../../../@core/components/buttons/TableButton.vue'
import SystemEmployeeForm from '../../../@core/components/table-from/SystemEmployeeForm.vue'

export default {
  name: 'AddNewEmployee',
  components: {
    TableButton,
    SystemEmployeeForm,
    customDropdown,
  },
  data() {
    return {
      status: [],
      user: null,
      search: null,
      role: null,
      userStatus: 'all',
      reporting_to_users: [],
      manage_by_users: [],
      userRole: null,
      reporting_to: null,
      manage_by: null,
      totalCount: 0,
      currentPage: 1,
      perPage: 10,
      loader: true,
      sortDesc: false,
      is_active: 'active',
      items: [],
      designation_id: null,
      shift_id: null,
      statusLoader: null,

      // Bulk assign shift variables
      selectedEmployees: [],
      selectAll: false,
      isIndeterminate: false,
      modalShiftId: null,
    }
  },
  computed: {
    fields_userList() {
      const commonFields = [
        {
          key: 'selected',
          label: '',
          thStyle: { width: '30px', textAlign: 'center' },
          tdStyle: { width: '30px', textAlign: 'center' },
          thClass: 'px-0',
          tdClass: 'px-0',
        },
        {
          key: 'emp_id',
          label: 'Employee Id',
          sortable: true,
          thStyle: { width: '100px' },
          tdStyle: { width: '100px' },
        },
        {
          key: 'name',
          label: 'Name',
          sortable: true,
          thStyle: { width: '300px' },
          tdStyle: { width: '300px' },
        },

        { key: 'phone', label: 'Phone', sortable: true },
        { key: 'users.full_name', label: 'Reporting To', sortable: true },
        { key: 'manage_by_user.full_name', label: 'Managed By', sortable: true },
        { key: 'inactive_date', label: 'Inactive Date', sortable: false },
        {
          key: 'shift',
          label: 'Shift',
          sortable: true,
          thStyle: { width: '110px' },
        },
        { key: 'working_mode', label: 'Working Mode', sortable: true },
        { key: 'is_active', label: 'Status', sortable: false },
      ]

      const specificFields = this.is_active === 'active' ? [] : []

      return [
        ...commonFields,
        ...specificFields,
        { key: 'action', label: 'Action', thClass: 'user-img', sortable: false },
      ]
    },
    statusList() {
      return [
        { label: 'All', value: 'all' },
        { label: 'Invited', value: 'invited' },
        { label: 'Joined', value: 'joined' },
      ]
    },
    roleList() {
      return [
        { label: 'Employee', value: 'EMP' },
        { label: 'Human Resources', value: 'HR' },
        { label: 'Manager', value: 'MN' },
        { label: 'Super Admin', value: 'SA' },
      ]
    },
    debouncedSearch: {
      get() {
        return this.search
      },
      set: debounce(function (value) {
        this.search = value
        this.getUserData()
      }, 750),
    },
    designations() {
      return this.$store.state.app.designationList.map(element => ({
        value: element.id,
        label: element.name,
      }))
    },
    shiftOptions() {
      return this.$store.state.app.shiftList.map(s => ({
        value: s.id,
        label: s.name,
      }))
    },
  },
  watch: {
    perPage() {
      if (this.currentPage === 1) {
        this.getUserData()
      } else {
        this.currentPage = 1
      }
    },
    currentPage: {
      handler(value) {
        if (value) {
          this.getUserData()
        }
      },
    },
  },
  mounted() {
    eventBus.$on('user', data => {
      if (data) {
        this.search = data.search
        this.is_active = data.is_active
        this.userRole = data.role
        this.userStatus = data.userstatus || 'all'
        this.manage_by = data.manage_by
        this.reporting_to = data.reporting_to
        this.designation_id = data.designation_id
        this.shift_id = data.shift_id || null
        this.getUserData()
        this.$root.$emit('bv::toggle::collapse', 'search')
      }
    })
    this.getUserData()
    this.filterFunctionCallFromMixin()
  },
  methods: {
    /**
     * @description Load filter dependencies (manage-by users, reporting-to users,
     *              designations, and shift list) on mount
     */
    async filterFunctionCallFromMixin() {
      this.manage_by_users = await this.manageByUserList()
      this.reporting_to_users = await this.reportingtoUserList()
      if (this.$store.state.app.designationList.length === 0) {
        await this.designationList()
      }
      if (this.$store.state.app.shiftList.length === 0) {
        const response = await this.getHTTPPostResponse(
          'admin/master/shift/list',
          { is_active: true, per_page: 500 },
          false,
        )
        if (response && response.data && response.data.shifts) {
          this.$store.commit('app/UPDATE_SHIFT_LIST', response.data.shifts)
        }
      }
    },

    /**
     * @description Fetch paginated employee list from API
     * @method POST
     * @returns items (employee list)
     */
    async getUserData() {
      this.loader = true
      const input = {
        reporting_to: this.reporting_to,
        manage_by: this.manage_by,
        page: this.currentPage,
        per_page: this.perPage,
        sort_field: 'name',
        sort_order: this.sortDesc ? 'desc' : 'asc',
        is_active:
          this.is_active && this.is_active !== 'all_data'
            ? this.is_active === 'active'
            : '',
        role: this.role,
        status: this.userStatus === 'all' ? '' : this.userStatus,
        search: this.search,
        designation_id: this.designation_id,
        shift_id: this.shift_id,
      }
      const response = await this.getHTTPPostResponse(
        'admin/master/system-employee/list',
        input,
        false,
      )
      if (response && response.data) {
        const { data } = response
        this.items = data.users
        this.totalCount = data.count
      }
      this.selectedEmployees = []
      this.selectAll = false
      this.isIndeterminate = false
      this.loader = false
    },

    /**
     * @description Open Edit sidebar with employee data
     * @param {Object} data employee record
     */
    edit(data) {
      this.$refs.systemEmployeeForm.openForEdit(data)
    },

    /**
     * @description Toggle employee active/inactive status
     * @method PUT
     * @param {Object} item employee record
     */
    async toggleStatus(item) {
      this.statusLoader = item.id
      const response = await this.getHTTPPutResponse(
        `admin/master/system-employee/status/${item.id}`,
        { active: item.is_active ? 0 : 1 },
        true,
      )
      if (response && response.status === 200) {
        await this.getUserData()
      }
      this.statusLoader = null
    },

    /**
     * @description Resend invitation e-mail to an invited employee
     * @param {Number} id user id
     */
    async sendInvitationMail(id) {
      this.$swal({
        title: 'Are you sure?',
        icon: 'info',
        html: '<p>You want to Send Invitation</p>',
        showCloseButton: true,
        showCancelButton: true,
        focusConfirm: false,
        confirmButtonText: 'Send',
        customClass: {
          confirmButton: 'btn confirm',
          cancelButton: 'btn cancel ml-1',
        },
        buttonsStyling: false,
      }).then(value => {
        if (value.isConfirmed === true) {
          const input = { user_id: id }
          const response = this.getHTTPPostResponse(
            'admin/master/user/resend-invitation',
            input,
            true,
          )
          if (response) {
            this.getUserData()
          }
        }
      })
    },

    /** Clear all filters and reload list */
    clearFilter() {
      this.status = []
      this.manage_by = null
      this.reporting_to = null
      this.search = null
      this.is_active = 'active'
      this.role = null
      this.userStatus = 'all'
      this.perPage = 10
      this.designation_id = null
      this.shift_id = null
      this.getUserData()
    },
    toggleSelectAll(checked) {
      if (checked) {
        this.selectedEmployees = this.items.map(item => item.id)
      } else {
        this.selectedEmployees = []
      }
      this.isIndeterminate = false
    },
    onSelectionChange() {
      if (this.selectedEmployees.length === 0) {
        this.selectAll = false
        this.isIndeterminate = false
      } else if (this.selectedEmployees.length === this.items.length) {
        this.selectAll = true
        this.isIndeterminate = false
      } else {
        this.selectAll = false
        this.isIndeterminate = true
      }
    },
    resetModal() {
      this.modalShiftId = null
    },
    async submitBulkAssign(bvEvent) {
      if (bvEvent && bvEvent.preventDefault) {
        bvEvent.preventDefault()
      }

      if (this.selectedEmployees.length === 0) {
        this.$toast.error('No employees selected.')
        return
      }

      const payload = {
        employee_ids: this.selectedEmployees,
        shift_id: this.modalShiftId,
      }

      const response = await this.getHTTPPostResponse(
        'admin/master/system-employee/bulk-assign-shift',
        payload,
        true,
      )

      if (response && response.status === 200) {
        this.$bvModal.hide('bulkAssignShiftModal')
        this.selectedEmployees = []
        this.selectAll = false
        this.isIndeterminate = false
        this.getUserData()
      }
    },
  },
}
